<script>
import UiHint from './Hint';
import moment from 'moment';
import debounce from 'debounce';

export default {
    components: {
        UiHint,
    },
    props: {
        value: String,
        hint: String,
        label: String,
        type: {
            type: String,
            default: 'text',
        },
        fillHeight: Boolean,
        labelTop: Boolean,
        readonly: Boolean,
        debounce: {
            type: Number,
            default: 200,
        },
        popup: String,
        error: Boolean,
        disabled: {
            type: Boolean,
            default: false,
        },
        placeholder: String,
        maxLen: {
            type: Number,
            default: 0,
        },
        items: {
            type: Array,
            default: () => [],
        },
    },
    created() {
        const { lpad } = this;
        this.onInput = debounce(() => {
            if (this.type === 'datepicker') {
                this.flattenDate();
            }
            this.$emit('input', this.localValue);
        }, this.debounce);
        if (this.type === 'datepicker') {
            this.copyDate();
            this.flattenDate();
            this.onInput();
        } else {
            this.localValue = this.value;
        }
    },
    data() {
        return {
            localValue: null,
            y: 2019,
            m: 1,
            d: 1,
            focus: false,
        };
    },
    computed: {
        yearsValues() {
            const y = moment().year();
            return [0, 1, 2, 3, 4, 5].map(i => y + i);
        },
        monthsValues() {
            return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
        },
        daysValues() {
            const daysInMonth = moment().year(this.y).month(this.m - 1).daysInMonth();
            let result = [];
            for (let i = 0; i < daysInMonth; i++) {
                result.push(i + 1);
            }
            return result;
        },
    },
    methods: {
        lpad(number, digits) {
            return Array(Math.max(digits - String(number).length + 1, 0)).join(0) + number;
        },
        copyDate() {
            let currentDate = new Date();
            if (this.value) {
                try {
                    currentDate = new Date(this.value);
                } catch (e) {
                    currentDate = new Date();
                }
            }
            this.y = currentDate.getFullYear();
            this.m = currentDate.getMonth() + 1;
            this.d = currentDate.getDate();
        },
        flattenDate() {
            const { y, m, d, lpad } = this;
            this.localValue = `${y}-${lpad(m, 2)}-${lpad(d, 2)}`;
        },
        onFocus() {
            this.focus = true;
            this.$emit('focus');
        },
        onBlur() {
            this.focus = false;
            this.$emit('blur');
        }
    },
    watch: {
        value(nextVal) {
            if (nextVal !== this.localValue) {
                this.localValue = nextVal;
                if (this.type === 'datapicker') {
                    this.flattenDate();
                    this.onInput();
                }
            }
        },

    },
};
</script>

<template>
    <div :class="{Input: true, StateFocus: focus, FillHeight: this.fillHeight, StateError: this.error, LabelTop: this.labelTop}">
        <div class="Details">
            <label v-if="label">{{ label }}</label>
            <span v-if="hint">{{ hint }}</span>
            <span v-if="maxLen">{{ localValue.length }} / {{ maxLen }}</span>
            <slot name="details"></slot>
        </div>
        <div class="Control">
            <div class="DatePicker" v-if="type === 'datepicker'">
                <select v-model="y" @input="onInput" :disabled="disabled">
                    <option
                        v-for="i in yearsValues"
                        :value="i"
                        :key="i"
                    >{{ i }}</option>
                </select>
                <select v-model="m" @input="onInput" :disabled="disabled">
                    <option
                        v-for="i in monthsValues"
                        :value="i"
                        :key="i"
                    >{{ lpad(i, 2) }}</option>
                </select>
                <select v-model="d" @input="onInput" :disabled="disabled">
                    <option
                        v-for="i in daysValues"
                        :value="i"
                        :key="i"
                    >{{ lpad(i, 2) }}</option>
                </select>
            </div>
            <template v-else-if="type === 'textarea'">
                <textarea
                    v-model="localValue"
                    @input="onInput"
                    @focus="onFocus"
                    @blur="onBlur"
                    :readonly="readonly"
                    :disabled="disabled"
                    :maxlength="maxLen || undefined"
                    :placeholder="placeholder"
                />
            </template>
            <template v-else-if="type === 'select'">
                <select v-model="localValue" @input="onInput" :disabled="disabled">
                    <option
                        v-for="i in items"
                        :value="i.value"
                        :key="i.value"
                    >{{ i.title }}</option>
                </select>
            </template>
            <template v-else>
                <input
                    v-model="localValue"
                    :type="type"
                    @input="onInput"
                    @focus="onFocus"
                    @blur="onBlur"
                    :readonly="readonly"
                    :placeholder="placeholder"
                    :disabled="disabled"
                />
            </template>
            <slot name="append"></slot>
            <ui-hint v-if="popup" class="Darken NoWrap Abs">
                <span v-html="popup" />
            </ui-hint>
        </div>
    </div>
</template>

<style lang="stylus" scoped>
@import '../../stylus/vars.styl'

.Input
    font-family Montserrat
    display flex
    align-items centar
    justify-content flex-start
    background #fff
    border 1px solid $cBorder
    transition .3s all
    padding 18px 15px 18px 35px
    box-sizing border-box
    //position relative
    .Hint
        display none
        left 100%
        top 50%
        transform translate(0, -50%)
    .Details
        padding-right 10px
        color $cPrimary
        display flex
        flex-direction column
        justify-content center
        align-items flex-start
        width 40%
        label
            font-weight 700
            font-size 15px
        span
            font-weight 400
            font-size 12px

    .Control
        position relative
        padding-left 10px
        width 60%
        display flex
        flex-direction column
        justify-content center
        input, textarea, select
            color #c7c7c7
            font-size 15px
            box-sizing border-box
            border 3px solid $cBorder
            width 100%
            padding 20px
            transition .3s all
            font-weight 500
            background-color #fff
        textarea
            min-height 155px
        .DatePicker
            display flex
            select
                margin-right 20px
                &:last-child
                    margin 0

    &.FillHeight
        height 100%

    &.StyleAlt
        background transparent
        border-color transparent
        padding 18px 0
        text-align right
        .Details
            align-items flex-end
            //width 30%

        .Control
            width 70%
            //padding-right 0

    &.StyleAlt02
        background transparent
        border-color transparent
        padding 18px 0
        text-align right
        .Details
            width auto
        .Control
            width auto
            flex 1 1 auto

    &.PBS
        padding-bottom 10px

    &.PTS
        padding-top 10px

    &.PT0
        padding-top 0

    &.StateFocus:not(&.StateError)
        position relative
        .Hint
            display flex
        &:not(.StyleAlt):not(.StyleAlt02)
            box-shadow 0 30px 80px rgba(0,0,0,.1)
        .Control
            input, textarea, select
                border-color $cAccent
                color $cPrimary

    &.StateError
        input, textarea, select
            border-color $cError
            color $cError

    &.LabelTop
        .Details
            justify-content flex-start
            padding-top 20px
</style>
